import { driver, type DriveStep, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/CoachTour.css";
import type { TourRunnerOptions } from "./types";

const shouldAnimateTour = (): boolean => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return true;
  }

  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const getFocusedElement = (): HTMLElement | null => {
  if (typeof document === "undefined") {
    return null;
  }

  return document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
};

const createSmokeTestStep = (options: TourRunnerOptions): DriveStep => ({
  data: { variant: options.variant },
  popover: {
    title: options.copy.title,
    description: options.copy.description,
    showButtons: ["next", "close"],
  },
});

export class TourRunner {
  private activeDriver: Driver | null = null;

  start(options: TourRunnerOptions): void {
    this.destroy();
    if (!options.tour.variants.includes(options.variant)) {
      return;
    }

    let completed = false;
    const previouslyFocusedElement = getFocusedElement();
    const tourDriver = driver({
      animate: shouldAnimateTour(),
      duration: 320,
      allowClose: true,
      allowKeyboardControl: true,
      overlayOpacity: 0.35,
      popoverClass: "coach-tour-popover",
      popoverOffset: 16,
      showProgress: true,
      progressText: options.copy.progressText,
      nextBtnText: options.copy.nextButton,
      prevBtnText: options.copy.backButton,
      doneBtnText: options.copy.doneButton,
      onPopoverRender: (popover) => {
        popover.closeButton.setAttribute(
          "aria-label",
          options.copy.closeButton,
        );
        popover.closeButton.setAttribute("title", options.copy.closeButton);
      },
      onDoneClick: (_element, _step, hookOptions) => {
        completed = true;
        hookOptions.driver.destroy();
      },
      onCloseClick: (_element, _step, hookOptions) => {
        hookOptions.driver.destroy();
      },
      onDestroyed: (_element, _step, hookOptions) => {
        if (this.activeDriver === hookOptions.driver) {
          this.activeDriver = null;
        }
        options.onCleanup?.();
        if (previouslyFocusedElement?.isConnected) {
          previouslyFocusedElement.focus({ preventScroll: true });
        }
        if (completed) {
          void options.onComplete();
        }
      },
    });

    this.activeDriver = tourDriver;
    tourDriver.setSteps(options.steps ?? [createSmokeTestStep(options)]);
    tourDriver.drive();
  }

  destroy(): void {
    this.activeDriver?.destroy();
    this.activeDriver = null;
  }
}
